import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { TextField, FieldWrapper } from '@/components/forms/Field';
import { ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { permissionsApi, rolesApi } from '@/services/api/endpoints/identity';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { Role } from '@/types/domain';
import { humanise } from '@/utils/format';

const schema = z.object({
  code: z
    .string()
    .min(1, 'Code is required')
    .max(64)
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Use upper-case letters, digits and underscores'),
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500),
  permissionIds: z.array(z.string().uuid()),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = { code: '', name: '', description: '', permissionIds: [] };

/**
 * Roles and the permissions they grant.
 *
 * Built-in roles are shown but cannot be edited or deleted: they are shared by
 * every school, so letting one tenant change them would alter another's access
 * model. A school defines its own roles alongside them.
 *
 * The permission set is submitted in full rather than as deltas, so two
 * administrators saving concurrently cannot merge into a set neither intended.
 */
export function RolesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<Role | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Role | null>(null);

  const roles = useQuery({ queryKey: queryKeys.roles.all, queryFn: rolesApi.list });
  const permissions = useQuery({
    queryKey: queryKeys.permissions.all,
    queryFn: permissionsApi.list,
    enabled: can(PERMISSIONS.PERMISSION_VIEW),
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = { ...values, description: values.description === '' ? undefined : values.description };
      return editing === null ? rolesApi.create(payload) : rolesApi.update(editing.id, payload);
    },
    onSuccess: () => {
      toast.success(editing === null ? 'Role created' : 'Role updated');
      close();
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
    },
    onError: (error) => handleApiError(error, form.setError),
  });

  const remove = useMutation({
    mutationFn: (role: Role) => rolesApi.remove(role.id),
    onSuccess: () => {
      toast.success('Role deleted');
      setDeleting(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
    },
    onError: (error) => {
      // Typically RESOURCE_IN_USE: users still hold the role.
      handleApiError(error);
      setDeleting(null);
    },
  });

  function openCreate() {
    setEditing(null);
    form.reset(EMPTY);
    setOpen(true);
  }

  function openEdit(role: Role) {
    setEditing(role);
    form.reset({
      code: role.code,
      name: role.name,
      description: role.description ?? '',
      permissionIds: role.permissions.map((permission) => permission.id),
    });
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setEditing(null);
  }

  if (roles.isPending) {
    return <LoadingState label="Loading roles" />;
  }
  if (roles.error !== null) {
    return <ErrorState error={roles.error} onRetry={() => void roles.refetch()} />;
  }

  const selected = form.watch('permissionIds');

  const byResource = new Map<string, typeof permissions.data>();
  for (const permission of permissions.data ?? []) {
    const bucket = byResource.get(permission.resource) ?? [];
    bucket.push(permission);
    byResource.set(permission.resource, bucket);
  }

  return (
    <>
      <PageHeader
        title="Roles"
        description="A role bundles permissions. Built-in roles are shared by every school and cannot be changed."
        action={can(PERMISSIONS.ROLE_CREATE) ? <Button onClick={openCreate}>New role</Button> : undefined}
      />

      <div className="grid gap-4 md:grid-cols-2">
        {roles.data.map((role) => (
          <Card key={role.id}>
            <CardHeader
              title={role.name}
              description={role.description}
              action={
                <div className="flex items-center gap-2">
                  {role.system ? (
                    <Badge tone="info">Built-in</Badge>
                  ) : (
                    <>
                      {can(PERMISSIONS.ROLE_UPDATE) && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(role)}>
                          Edit
                        </Button>
                      )}
                      {can(PERMISSIONS.ROLE_DELETE) && (
                        <Button size="sm" variant="ghost" onClick={() => setDeleting(role)}>
                          Delete
                        </Button>
                      )}
                    </>
                  )}
                </div>
              }
            />
            <div className="px-5 py-4">
              <p className="mb-2 font-mono text-xs text-subtle">{role.code}</p>
              <p className="mb-2 text-xs text-muted">
                {role.permissions.length} permission{role.permissions.length === 1 ? '' : 's'}
              </p>
              <div className="flex flex-wrap gap-1">
                {role.permissions.slice(0, 12).map((permission) => (
                  <Badge key={permission.id}>{permission.code}</Badge>
                ))}
                {role.permissions.length > 12 && (
                  <Badge tone="neutral">+{role.permissions.length - 12} more</Badge>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Modal
        open={open}
        size="lg"
        title={editing === null ? 'New role' : `Edit ${editing.name}`}
        description="The permission set is saved in full, replacing whatever the role had before."
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button onClick={form.handleSubmit((values) => save.mutate(values))} loading={save.isPending}>
              Save
            </Button>
          </>
        }
      >
        <form className="space-y-4" noValidate onSubmit={form.handleSubmit((values) => save.mutate(values))}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Code"
              required
              hint="e.g. LIBRARIAN"
              error={form.formState.errors.code?.message}
              {...form.register('code')}
            />
            <TextField label="Name" required error={form.formState.errors.name?.message} {...form.register('name')} />
          </div>
          <TextField
            label="Description"
            error={form.formState.errors.description?.message}
            {...form.register('description')}
          />

          <FieldWrapper label="Permissions" htmlFor="permissionIds">
            <div className="max-h-80 space-y-4 overflow-y-auto rounded-md border border-line p-3">
              {[...byResource.entries()]
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([resource, items]) => (
                  <div key={resource}>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-subtle">
                      {humanise(resource)}
                    </p>
                    <div className="grid gap-1 sm:grid-cols-2">
                      {(items ?? []).map((permission) => (
                        <label
                          key={permission.id}
                          className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-surface-2"                        >
                          <input
                            type="checkbox"
                            className=""                            checked={selected.includes(permission.id)}
                            onChange={(event) => {
                              const next = event.target.checked
                                ? [...selected, permission.id]
                                : selected.filter((id) => id !== permission.id);
                              form.setValue('permissionIds', next, { shouldValidate: true });
                            }}
                          />
                          <span className="text-muted">{permission.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </FieldWrapper>
        </form>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete role"
        message={
          deleting === null
            ? ''
            : `Delete ${deleting.name}? This is refused while any user still holds it.`
        }
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => deleting !== null && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
